import {
  type AppleCalendar,
  type CalendarChoice,
  type CalendarListResult,
  type HiddenIds,
  hiddenIds,
  MACOS_CALENDAR_BLOCKED_ACCOUNTS,
  MACOS_CALENDAR_BLOCKED_CALENDARS,
  transportEnv,
} from '@littlebird/apple-calendar/contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { electronApis } from '@/features/electron-api';
import {
  AXON_MCP_SERVERS_QUERY_KEY,
  useMcpServersQuery,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';
import { inBrowser } from '@/utils/capabilities';

import type { ServerEntry, ServerEntryInput } from '@littlebird/axon-mcp';

export const CALENDARS_QUERY_KEY = [
  'integration-configuration',
  'apple-calendar',
  'calendars',
] as const;
export const MEETINGS_OFF_QUERY_KEY = [
  'integration-configuration',
  'apple-calendar',
  'meetings-off',
] as const;

export type AccessTarget =
  | { kind: 'calendar'; calendar: AppleCalendar }
  | { kind: 'account'; accountId: string; calendars: AppleCalendar[] };

export function choiceFor(
  calendar: AppleCalendar,
  hidden: HiddenIds,
  meetingsOff: Set<string>
): CalendarChoice {
  if (
    hidden.calendars.has(calendar.id) ||
    hidden.accounts.has(calendar.account.id)
  ) {
    return 'hidden';
  }
  return !calendar.editable || meetingsOff.has(calendar.id)
    ? 'search'
    : 'meetings';
}

export function accountChoice(
  calendars: AppleCalendar[],
  hidden: HiddenIds,
  meetingsOff: Set<string>
): CalendarChoice | 'mixed' {
  const choices = calendars.map((c) => choiceFor(c, hidden, meetingsOff));
  // a visible view-only calendar is always Search, so it matches Meetings and search too
  const flexible = (choice: CalendarChoice, index: number) =>
    !calendars[index].editable && choice !== 'hidden';
  const decisive = new Set(choices.filter((choice, i) => !flexible(choice, i)));
  const hasFlexible = choices.some(flexible);
  if (decisive.size === 0) return hasFlexible ? 'search' : 'mixed';
  if (decisive.size > 1) return 'mixed';
  const [choice] = decisive;
  return choice === 'hidden' && hasFlexible ? 'mixed' : choice;
}

function withHiddenIds(
  server: ServerEntry,
  hidden: HiddenIds
): Partial<ServerEntryInput> {
  if (server.transport.kind === 'http') return {};
  const env = Object.fromEntries(
    Object.entries(transportEnv(server)).filter(
      ([key]) =>
        key !== MACOS_CALENDAR_BLOCKED_ACCOUNTS &&
        key !== MACOS_CALENDAR_BLOCKED_CALENDARS
    )
  );
  if (hidden.accounts.size > 0) {
    env[MACOS_CALENDAR_BLOCKED_ACCOUNTS] = [...hidden.accounts].join(',');
  }
  if (hidden.calendars.size > 0) {
    env[MACOS_CALENDAR_BLOCKED_CALENDARS] = [...hidden.calendars].join(',');
  }
  const { env: _oldEnv, ...transport } = server.transport;
  return {
    transport: Object.keys(env).length > 0 ? { ...transport, env } : transport,
  };
}

export function applyChoice(
  target: AccessTarget,
  choice: CalendarChoice,
  server: ServerEntry,
  meetingsOff: string[]
): { serverChanges: Partial<ServerEntryInput>; meetingsOff: string[] } {
  const hidden = hiddenIds(server);
  const off = new Set(meetingsOff);

  if (target.kind === 'calendar') {
    const { calendar } = target;
    if (choice === 'meetings' && !calendar.editable) {
      throw new Error('A view-only calendar cannot be used for meetings');
    }
    if (choice === 'hidden') {
      hidden.calendars.add(calendar.id);
    } else {
      hidden.calendars.delete(calendar.id);
      if (calendar.editable) {
        if (choice === 'search') off.add(calendar.id);
        else off.delete(calendar.id);
      }
    }
  } else if (choice === 'hidden') {
    hidden.accounts.add(target.accountId);
  } else {
    hidden.accounts.delete(target.accountId);
    for (const calendar of target.calendars) {
      hidden.calendars.delete(calendar.id);
      if (!calendar.editable) continue;
      if (choice === 'search') off.add(calendar.id);
      else off.delete(calendar.id);
    }
  }

  return {
    serverChanges: withHiddenIds(server, hidden),
    meetingsOff: [...off],
  };
}

export function displayNames(calendars: AppleCalendar[]): Map<string, string> {
  const groups = new Map<string, AppleCalendar[]>();
  for (const calendar of calendars) {
    const key = `${calendar.account.id}\u0000${calendar.name}`;
    groups.set(key, [...(groups.get(key) ?? []), calendar]);
  }
  const names = new Map<string, string>();
  for (const group of groups.values()) {
    const sorted = [...group].sort((a, b) => a.id.localeCompare(b.id));
    sorted.forEach((calendar, index) => {
      names.set(
        calendar.id,
        sorted.length > 1 ? `${calendar.name} (${index + 1})` : calendar.name
      );
    });
  }
  return names;
}

export function summary(
  calendars: AppleCalendar[],
  hidden: HiddenIds,
  meetingsOff: Set<string>
): { visible: number; total: number; meetings: number } {
  const choices = calendars.map((c) => choiceFor(c, hidden, meetingsOff));
  return {
    visible: choices.filter((c) => c !== 'hidden').length,
    total: calendars.length,
    meetings: choices.filter((c) => c === 'meetings').length,
  };
}

const calendarIpc = {
  listCalendars: (slug: string) => electronApis.axonMcp.listCalendars(slug),
  getMeetingsOff: () => electronApis.axonMcp.getMeetingsOff(),
  setMeetingsOff: (ids: string[]) => electronApis.axonMcp.setMeetingsOff(ids),
};

const isAccessDenied = (listing: CalendarListResult | undefined) =>
  listing?.ok === false && listing.problem === 'access_denied';

export function useAppleCalendarAccess(serverSlug: string | undefined) {
  const serversQuery = useMcpServersQuery();
  const update = useUpdateMcpServerMutation();
  const queryClient = useQueryClient();
  const enabled = serverSlug !== undefined && !inBrowser();

  const calendarsQuery = useQuery({
    queryKey: [...CALENDARS_QUERY_KEY, serverSlug],
    queryFn: () => calendarIpc.listCalendars(serverSlug ?? ''),
    // listing may prompt for Calendar access; once per app session
    staleTime: Infinity,
    retry: false,
    // macOS can report access before the server gets it, so a denial is re-checked on focus
    refetchOnWindowFocus: (query) =>
      isAccessDenied(query.state.data) ? 'always' : false,
    enabled,
  });
  const meetingsOffQuery = useQuery({
    queryKey: MEETINGS_OFF_QUERY_KEY,
    queryFn: () => calendarIpc.getMeetingsOff(),
    enabled,
  });

  useEffect(() => {
    if (!enabled) return;
    return window.mainProcessApi.onEventKitStateChanged((state) => {
      const queryKey = [...CALENDARS_QUERY_KEY, serverSlug];
      const listing = queryClient.getQueryData<CalendarListResult>(queryKey);
      if (isAccessDenied(listing) && state.calendarStatus === 'authorized') {
        void queryClient.refetchQueries({ queryKey });
      }
    });
  }, [enabled, queryClient, serverSlug]);

  const server = serversQuery.data?.find((entry) => entry.slug === serverSlug);

  const queue = useRef<Promise<void>>(Promise.resolve());
  const ready =
    calendarsQuery.data !== undefined &&
    meetingsOffQuery.data !== undefined &&
    server !== undefined;

  const applyQueued = async (target: AccessTarget, choice: CalendarChoice) => {
    // the latest cached entry, so a choice never drops one saved just before it
    const latest = queryClient
      .getQueryData<ServerEntry[]>(AXON_MCP_SERVERS_QUERY_KEY)
      ?.find((entry) => entry.slug === serverSlug);
    const previous = queryClient.getQueryData<string[]>(MEETINGS_OFF_QUERY_KEY);
    if (!latest || latest.transport.kind === 'http' || !previous) return;
    const next = applyChoice(target, choice, latest, previous);
    try {
      await update.mutateAsync({
        slug: latest.slug,
        changes: next.serverChanges,
      });
    } catch {
      return;
    }
    queryClient.setQueryData(MEETINGS_OFF_QUERY_KEY, next.meetingsOff);
    try {
      await calendarIpc.setMeetingsOff(next.meetingsOff);
    } catch {
      queryClient.setQueryData(MEETINGS_OFF_QUERY_KEY, previous);
      await queryClient.invalidateQueries({ queryKey: MEETINGS_OFF_QUERY_KEY });
    }
  };

  const setChoice = (target: AccessTarget, choice: CalendarChoice) => {
    if (!ready) return;
    queue.current = queue.current
      .then(() => applyQueued(target, choice))
      .catch((error: unknown) => {
        console.warn('Apple Calendar choice failed', error);
      });
  };

  const meetingsOff = new Set(meetingsOffQuery.data ?? []);
  return {
    calendarsQuery,
    hidden: server
      ? hiddenIds(server)
      : { accounts: new Set<string>(), calendars: new Set<string>() },
    meetingsOff,
    ready,
    saving: update.isPending,
    setChoice,
  };
}

export function groupByAccount(calendars: AppleCalendar[]) {
  const groups = new Map<
    string,
    { id: string; name: string; calendars: AppleCalendar[] }
  >();
  for (const calendar of calendars) {
    const group = groups.get(calendar.account.id) ?? {
      id: calendar.account.id,
      name: calendar.account.name,
      calendars: [],
    };
    group.calendars.push(calendar);
    groups.set(calendar.account.id, group);
  }
  return [...groups.values()];
}
